import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Check, Sparkles, FileText, Zap, Shield, Globe } from 'lucide-react';
import { Container, Section, Badge, Button } from '../ui';
import { PRODUCTS } from '../../constants';
import { useIntersectionObserver } from '../../hooks';

export const Products: React.FC = () => {
  const { elementRef, isVisible } = useIntersectionObserver({ threshold: 0.1 });

  return (
    <Section background="mesh" padding="lg" id="products">
      <Container>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="accent" className="mb-4">Our Products</Badge>
          <h2 className="heading-lg text-navy-900 mb-4">
            Innovative Solutions Built by ISTATEC
          </h2>
          <p className="text-body text-lg">
            Discover our suite of AI-powered products designed to solve real business 
            challenges and deliver measurable results.
          </p>
        </div>

        {/* Featured Product - FinExtract */}
        {PRODUCTS.map((product) => (
          <motion.div
            key={product.id}
            ref={elementRef}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900"
            initial={{ opacity: 0, y: 40 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.6 }}
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-accent-500/10 to-transparent" />

            <div className="relative z-10 grid lg:grid-cols-2 gap-12 p-8 md:p-12 lg:p-16">
              {/* Content */}
              <div className="flex flex-col justify-center">
                <Badge className="w-fit mb-6 bg-accent-500/20 text-accent-300 border border-accent-500/30">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Featured Product
                </Badge>

                <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  {product.name}
                </h3>
                <p className="text-xl text-accent-300 mb-4">
                  {product.tagline}
                </p>
                <p className="text-navy-200 text-lg mb-8 leading-relaxed">
                  {product.description}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {product.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-white">
                      <span className="w-6 h-6 rounded-full bg-accent-500/20 flex items-center justify-center">
                        <Check className="w-4 h-4 text-accent-400" />
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className="flex flex-wrap gap-4">
                  <a href={product.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="accent" size="lg" rightIcon={<ExternalLink className="w-5 h-5" />}>
                      Try FinExtract
                    </Button>
                  </a>
                  <a href={`${product.url}/docs`} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" size="lg" className="text-white hover:bg-white/10 border border-white/20">
                      View Documentation
                    </Button>
                  </a>
                </div>
              </div>

              {/* Product Showcase */}
              <div className="flex items-center justify-center">
                <motion.div
                  className="relative w-full max-w-md"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                >
                  {/* Mockup Card */}
                  <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-navy-50 px-6 py-4 border-b border-navy-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-navy-900">FinExtract API</div>
                          <div className="text-sm text-navy-500">Parse credit card statements</div>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4">
                      {/* Feature Cards */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-4 bg-navy-50 rounded-xl">
                          <Zap className="w-6 h-6 text-accent-500 mb-2" />
                          <div className="font-semibold text-navy-900 text-sm">Fast Processing</div>
                          <div className="text-xs text-navy-500">Under 2 seconds</div>
                        </div>
                        <div className="p-4 bg-navy-50 rounded-xl">
                          <Shield className="w-6 h-6 text-emerald-500 mb-2" />
                          <div className="font-semibold text-navy-900 text-sm">SOC 2 Compliant</div>
                          <div className="text-xs text-navy-500">Enterprise-ready</div>
                        </div>
                        <div className="p-4 bg-navy-50 rounded-xl">
                          <Globe className="w-6 h-6 text-purple-500 mb-2" />
                          <div className="font-semibold text-navy-900 text-sm">50+ Banks</div>
                          <div className="text-xs text-navy-500">Wide coverage</div>
                        </div>
                        <div className="p-4 bg-navy-50 rounded-xl">
                          <Check className="w-6 h-6 text-blue-500 mb-2" />
                          <div className="font-semibold text-navy-900 text-sm">99.9% Accuracy</div>
                          <div className="text-xs text-navy-500">AI-powered</div>
                        </div>
                      </div>

                      {/* Code Preview */}
                      <div className="bg-navy-900 rounded-xl p-4 font-mono text-xs">
                        <div className="text-navy-400">// Extract financial data</div>
                        <div className="text-accent-300">const</div>
                        <span className="text-white"> result = </span>
                        <span className="text-accent-300">await</span>
                        <span className="text-white"> finextract.</span>
                        <span className="text-emerald-400">parse</span>
                        <span className="text-white">(pdf);</span>
                      </div>
                    </div>
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent-500/30 rounded-full blur-2xl" />
                  <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl" />
                </motion.div>
              </div>
            </div>
          </motion.div>
        ))}
      </Container>
    </Section>
  );
};

